import React from 'react';
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';


import { useI18n } from '@/i18n/LanguageContext';
import { ADDRESS_LINES, ADDRESS_ONE_LINE, BRAND, TRADES, TRUST_PAGES, brandDescription, tradeLabel } from '@/data/constants';

import { TOP_COUNTY_SLUGS } from '@/data/seo/locations';

import LanguageToggle from './LanguageToggle';
import Logo from './brand/Logo';
import LeadCaptureForm from './marketing/LeadCaptureForm';


const Footer: React.FC = () => {
  const { t, path, lang } = useI18n();

  return (
    <footer className="mt-20 bg-slate-900 text-slate-300">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <Link to={path('/')} aria-label="GIGGONE" className="inline-block">
              <Logo tone="dark" size={36} />
            </Link>
            {/* Official company description — single source of truth in src/data/constants.ts */}
            <p className="mt-3 max-w-sm text-sm text-slate-300">{brandDescription(lang)}</p>
            <p className="mt-2 max-w-sm text-sm text-slate-500">{t('footer.builtFor')}</p>

            {/* ------------------------------------------------------------
                OPERATED BY — relocated here from the middle of the home page.
                Company identity belongs with the rest of the legal/company
                information, not between two marketplace sections. It stays on
                every page, which is better for reputation scanners than a
                home-page-only block.
            ------------------------------------------------------------- */}
            <div className="mt-5 rounded-xl border border-white/10 bg-white/5 p-4">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                {t('home.trustOperator')}
              </p>
              <address className="mt-1 text-sm not-italic leading-relaxed text-slate-300">
                <span className="block font-bold text-white">{BRAND.fullName}</span>
                {ADDRESS_LINES.map((line) => (
                  <span key={line} className="block">
                    {line}
                  </span>
                ))}
              </address>
              <a
                href={`mailto:${BRAND.email}`}
                className="mt-1 inline-block text-sm font-semibold text-orange-400 hover:text-orange-300"
              >
                {BRAND.email}
              </a>
            </div>



            {/* ------------------------------------------------------------
                VISITOR LEAD CAPTURE — shared component, present on EVERY page.
                Syncs to GoHighLevel (contact upsert + audience/SMS tags +
                UTM attribution) and to the platform job-alert list in one go.
            ------------------------------------------------------------- */}
            <LeadCaptureForm
              variant="inline"
              audience="visitor"
              source="footer-signup"
              askTrade
              tags={['newsletter', 'job-alerts', `lang:${lang}`]}
              className="mt-6 max-w-sm"
            />
          </div>


          <div>
            <h3 className="text-sm font-bold uppercase tracking-wide text-white">{t('footer.resources')}</h3>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link to={path('/jobs')} className="hover:text-white">{t('nav.jobs')}</Link></li>
              <li><Link to={path('/leads')} className="hover:text-white">{t('seo.leadsHub')}</Link></li>
              <li><Link to={path('/pricing')} className="hover:text-white">{t('nav.pricing')}</Link></li>
              <li><Link to={path('/memberships')} className="hover:text-white">{t('seo.memberTitle')}</Link></li>
              <li><Link to={path('/resources')} className="hover:text-white">{t('seo.resourcesTitle')}</Link></li>
              {/* Public customer reviews wall + submit form. */}
              <li><Link to={path('/testimonials')} className="hover:text-white">{t('testi.navLabel')}</Link></li>

              <li><Link to={path('/how-it-works')} className="hover:text-white">{t('nav.howItWorks')}</Link></li>
              <li><Link to={path('/faq')} className="hover:text-white">{t('seo.faqTitle')}</Link></li>
              <li><Link to={path('/help')} className="hover:text-white">{t('seo.helpTitle')}</Link></li>
              <li><Link to={path('/subcontractors')} className="hover:text-white">{t('footer.forSubs')}</Link></li>
              <li><Link to={path('/homeowners')} className="hover:text-white">{t('footer.forHomeowners')}</Link></li>
              <li><Link to={path('/commercial')} className="hover:text-white">{t('footer.forCommercial')}</Link></li>
              <li><Link to={path('/general-contractors')} className="hover:text-white">{t('footer.forGcs')}</Link></li>

            </ul>
          </div>

          <div>
            <h3 className="text-sm font-bold uppercase tracking-wide text-white">{t('common.trade')}</h3>
            <ul className="mt-4 space-y-2 text-sm">
              {TRADES.slice(0, 8).map((tr) => (
                <li key={tr.slug}>
                  <Link to={path(`/trades/${tr.slug}`)} className="hover:text-white">
                    {tradeLabel(tr.slug, lang)}
                  </Link>
                </li>
              ))}
              <li>
                <Link to={path('/trades')} className="font-semibold text-orange-400 hover:text-orange-300">
                  {t('seo.allTrades')}
                </Link>
              </li>
            </ul>

            <h3 className="mt-6 text-sm font-bold uppercase tracking-wide text-white">{t('seo.byCounty')}</h3>
            <ul className="mt-4 space-y-2 text-sm">
              {TOP_COUNTY_SLUGS.slice(0, 5).map((slug) => (
                <li key={slug}>
                  <Link to={path(`/california/counties/${slug}`)} className="hover:text-white">
                    {slug.replace(/-county$/, '').replace(/(^|-)([a-z])/g, (_m, p1, p2) => (p1 ? ' ' : '') + p2.toUpperCase())} County
                  </Link>
                </li>
              ))}
              <li>
                <Link to={path('/california')} className="font-semibold text-orange-400 hover:text-orange-300">
                  {t('seo.allCounties')}
                </Link>
              </li>
            </ul>
          </div>


          <div>
            <h3 className="text-sm font-bold uppercase tracking-wide text-white">{t('footer.company')}</h3>
            <ul className="mt-4 space-y-2 text-sm">
              {/* The four public trust pages — no login required on any of them. */}
              <li>
                <Link to={path('/about')} className="font-semibold text-white hover:text-orange-400">
                  {t('footer.aboutUs')}
                </Link>
              </li>
              <li>
                <Link to={path('/contact')} className="font-semibold text-white hover:text-orange-400">
                  {t('footer.contactUs')}
                </Link>
              </li>
              <li>
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{t('footer.emailUs')}</p>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-slate-500" />
                <a href={`mailto:${BRAND.email}`} className="hover:text-white">
                  {BRAND.email}
                </a>
              </li>
              {/* No public support phone number — email is the only public channel. */}
              {/* The street address is NOT repeated here: it is rendered once,
                  in the "Operated by" block in the first column. Printing the
                  same address twice in one footer was part of the duplication
                  the home page audit flagged. */}

            </ul>


            <h3 className="mt-6 text-sm font-bold uppercase tracking-wide text-white">{t('footer.legal')}</h3>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link to={path('/privacy')} className="hover:text-white">{t('footer.privacy')}</Link></li>
              <li><Link to={path('/terms')} className="hover:text-white">{t('footer.terms')}</Link></li>
            </ul>
          </div>
        </div>

        {/* ------------------------------------------------------------------
            TRUST BAR — About / Contact / Privacy / Terms rendered from the
            shared TRUST_PAGES list so all four are guaranteed present in the
            footer of every page. Public, crawlable, no authentication.
        ------------------------------------------------------------------- */}
        <nav aria-label={t('footer.trustNav')} className="mt-10 border-t border-white/10 pt-6">
          <ul className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            {TRUST_PAGES.map((p) => (
              <li key={p.to}>
                <Link to={path(p.to)} className="font-semibold text-slate-300 underline-offset-4 hover:text-white hover:underline">
                  {t(p.key)}
                </Link>
              </li>
            ))}
            <li className="flex items-center gap-2 text-slate-400">
              <Mail className="h-4 w-4 text-slate-500" />
              <a href={`mailto:${BRAND.email}`} className="hover:text-white">
                {BRAND.email}
              </a>
            </li>
          </ul>
        </nav>

        <div className="mt-6 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-6 sm:flex-row">
          <div className="text-center sm:text-left">
            <p className="text-xs text-slate-500">
              © {new Date().getFullYear()} {BRAND.fullName}. {t('footer.rights')}
            </p>
            <p className="mt-1 text-xs text-slate-500">{brandDescription(lang)}</p>
            {/* One-line address in the legal bar so it appears on every page even
                if the company column is collapsed on small screens. */}
            <p className="mt-1 text-xs text-slate-500">{ADDRESS_ONE_LINE}</p>
          </div>

          <LanguageToggle />
        </div>
      </div>
    </footer>
  );
};

export default Footer;
